import React from "react";
import PropTypes from "prop-types";
import { v4 } from "uuid";

import { Paper, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import { Icon, Label } from "semantic-ui-react";

const useStyles = makeStyles({
  error: {
    "& > label": { color: "#BF3333 !important" },
    "& > div > fieldset": { borderColor: "#BF3333 !important" },
    "& .MuiOutlinedInput-root": { backgroundColor: "#BF333305 !important" },
    "& > div > input::placeholder": {
      color: "#BF3333 !important",
    },
    "& > div > input::placeholder::focus": {
      color: "#BF3333 !important",
    },
  },
  dropdownClass: {
    "& .MuiOutlinedInput-root": {
      "&": {
        backgroundColor: "white",
        position: "relative",
        paddingRight: "25px !important",
      },
      "& fieldset": {
        fontSize: "12.25px",
        border: "1px solid #dddedf",
        padding: 0,
      },
      "&:hover fieldset": {
        border: "1px solid #dddedf",
      },
      "&.Mui-focused fieldset": {
        border: "1px solid #95c8da",
      },
      "& MuiInputBase-input": {
        fontSize: ".8rem",
      },
    },
  },
  paper: {
    border: "1px solid #95c8da",
    fontSize: "1rem",
    fontFamily: "Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif",
  },
  tag: {
    cursor: "pointer !important",
    userSelect: "none !important",
    display: "inline-block !important",
    verticalAlign: "top !important",
    whiteSpace: "normal !important",
    fontSize: "1em !important",
    padding: "0.35714286em 0.78571429em !important",
    margin: "0.14285714rem 0.28571429rem 0.14285714rem 0em !important",
    boxShadow: "0px 0px 0px 1px rgba(34, 36, 38, 0.15) inset !important",
  },
  popupIndicator: {
    color: "rgba(0, 0, 0, 0.87)",
    opacity: "0.8",
  },
  listbox: {
    "&": {
      paddingTop: "0",
      paddingBottom: "0",
    },
    "& .MuiAutocomplete-option": {
      "&:first-child": {
        padding: "0.78571429rem 1.14285714rem !important",
        fontWeight: "400",
      },
      "&:not(:first-child)": {
        borderTop: "1px solid #fafafa",
        padding: "0.78571429rem 1.14285714rem !important",
        fontWeight: "400",
      },
      "&[aria-selected=true]": {
        fontWeight: "700",
        backgroundColor: "rgba(0, 0, 0, 0.08)",
      },
    },
  },
});

export function Dropdown({
  options,
  placeholder,
  error,
  getTagValue,
  ...restProps
}) {
  const classes = useStyles();
  return (
    <Autocomplete
      options={options}
      classes={{
        root: classes.dropdownClass,
        paper: classes.paper,
        popper: classes.popper,
        tag: classes.tag,
        popupIndicator: classes.popupIndicator,
        listbox: classes.listbox,
      }}
      PaperComponent={(props) => {
        return <Paper {...props} className={classes.paper} elevation={0} />;
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            error={error}
            classes={{ root: error ? classes.error : "" }}
            variant="outlined"
            placeholder={placeholder}
            size="small"
          />
        );
      }}
      renderTags={(values, getTagProps) => {
        return values.map((value, index) => {
          const { onDelete, disabled, ...tagProps } = getTagProps({ index });
          return (
            <Label
              key={v4()}
              style={
                disabled
                  ? { cursor: "default", pointerEvents: "none", opacity: ".45" }
                  : {}
              }
              {...tagProps}
            >
              {getTagValue(value)}
              <Icon name="delete" onClick={onDelete} disabled={disabled} />
            </Label>
          );
        });
      }}
      {...restProps}
    />
  );
}

Dropdown.propTypes = {
  options: PropTypes.array,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  getTagValue: PropTypes.func,
};

Dropdown.defaultProps = {
  options: [],
  placeholder: "Select a value",
  error: false,
  getTagValue: (value) => {
    return value;
  },
};

export default Dropdown;
